<template>
  <Header
    id="email-preview"
    :title="templateName"
    :is-modal="isModalDialog"
    :back-title="
        currentRouteName === routeNames.EMAIL_TEMPLATE_BUILDER
          ? $t('common.back')
          : $t('common.backToEmailBuilder')"
    :menu-options="templateOptions"
    :content-editable="true"
    :disable-restore="disableRestoreVersionBtn"
    :show-clone-template="cloneTemplate"
    @model:close="handleModalClose"
    @title:update="onTemplateNameChange"
    @route:back="onBack"
    @menu:click="onMenuItemClick"
    @save:click="saveCampaignTemplate"
    @saveAsTemplate:click="saveAsNewTemplate"
    @version:restore="$emit('switchVersion')"
    @contact:click="$emit('contact:click')"
  />
  
  <teleport to="body">
    <SendTestEmail
      v-if="openTestEmailSideCover"
      :show="openTestEmailSideCover"
      @close="() => (openTestEmailSideCover = false)"
      class="z-20 fonts-fallback"
      :hasRSSElement="hasRSSElement"
      :templateId="templateId"
      :userId="userId"
      :locationId="locationId"
      :service="service"
      :customFieldsLocation="customFields"
      :customValuesLocation="customValues"
      :schedule-details="templateData"
      :sender-preferences="senderPreferences"
      @update-sender-preference="(val:any)=>$emit('updateSenderPreference',val)"
    />
  </teleport>
  <teleport to="body"
    ><SaveTemplateSettings
      v-if="openSaveTemplateSettings"
      :show="openSaveTemplateSettings"
      @close="() => (openSaveTemplateSettings = false)"
      class="z-20 fonts-fallback"
      :templateId="templateId"
      :userId="userId"
      :locationId="locationId"
      :customFieldsLocation="customFields"
      :customValuesLocation="customValues"
  /></teleport>
  <CloneTemplateName
    :default-name="`${templateName}-copy`"
    :show="showCloneTemplateName"
    v-if="showCloneTemplateName"
    @close:modal="showCloneTemplateName = false"
    @clone:template="name => saveAsNewTemplate(name)"
  />
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref, watch } from 'vue'
import { useStore } from '../store'
import { UIButton, UIIcon, UISpinner } from '@gohighlevel/ghl-canvas'
import { useService } from '../service'

import { useBuilder, builderState } from 'common/composable/manageBuilderState'

import { useRouteRedirection } from '../composable/manageRedirect'

import { debounce } from '../util/helper'
import BuilderHeaderContext from 'common/components/BuilderHeaderContext.vue'
import { EMAIL_PREVIEW_ROUTES_NAMES } from 'common/utils/const_helper'
import {
  getBuilderContextActionsMenu,
  TEMPLATE_BUILDER_CONTEXT_MENU_IDs,
} from 'common/utils/menu_actions'
import SendTestEmail from 'common/components/Preview/SendTestEmail.vue'
import SaveTemplateButton from 'common/components/SaveTemplateButton.vue'
import RestoreVersionButton from 'common/components/RestoreTemplateVersionBtn.vue'
import { useDateFormat } from 'common/composable/filters'
import SaveTemplateSettings from '../components/SaveTemplateSettings.vue'
import { ActionTypes } from '../store/actions'
import Header from 'common/components/Header.vue'
import { useI18n } from 'vue-i18n'
import { appState } from '../../../common/composable/appState'
import CloneTemplateName from './CloneTemplateName.vue'

export default defineComponent({
  props: {
    disableRestoreVersionBtn: {
      type: Boolean,
      required: false,
      default: true,
    },
    senderPreferences: {
      type: Object,
      default: {},
    },
  },
  components: {
    UIButton,
    UIIcon,
    BuilderHeaderContext,
    UISpinner,
    SendTestEmail,
    SaveTemplateButton,
    RestoreVersionButton,
    SaveTemplateSettings,
    Header,
    CloneTemplateName,
  },
  setup() {
    const emitter: any = inject('emitter')
    const store = useStore()
    const service = useService()
    const { saveEditor } = useBuilder(store)
    const { t } = useI18n()

    const { routeBack } = useRouteRedirection()
    const currentRouteName = ref(appState.selectedPage)
    const openTestEmailSideCover = ref(false)
    const openSaveTemplateSettings = ref(false)
    const isModalDialog = computed(() => appState.isModalDialog)
    const showCloneTemplateName = ref(false)
    const cloneTemplate = computed(() =>
      appState.cloneTemplate
    )
    const { formatDate } = useDateFormat()
    const templateName = ref(appState.templateName)
    const templateData = computed(() => store.state.templateData)
    async function onTemplateNameChange(newName: string) {
      if (appState.templateId)
        debounce(async () => {
          if (newName) {
            const templateId = appState.templateId as string
            const locationId = appState.locationId as string
            const params = {
              name: newName,
              locationId,
              updatedBy: store?.state?.userId,
            }
            const { data: newTemplateData } = await service.updateTemplate(
              templateId,
              params
            )
            store.dispatch(ActionTypes.UPDATE_TEMPLATE, newTemplateData)
          }
        }, 1000)()
    }
    async function onBack() {
      if (
        currentRouteName.value ===
        EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_TEMPLATE_BUILDER
      ) {
        routeBack()
      } else {
        appState.selectedPage = EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_TEMPLATE_BUILDER
      }
    }
    async function onMenuItemClick(item: any) {
      switch (item.id) {
        case TEMPLATE_BUILDER_CONTEXT_MENU_IDs.TEST_EMAIL: {
          openTestEmailSideCover.value = true
          builderState.unsavedChanges && saveCampaignTemplate()
          break
        }
        case TEMPLATE_BUILDER_CONTEXT_MENU_IDs.PREVIEW_CAMPAIGN: {
          builderState.unsavedChanges && saveCampaignTemplate()
          appState.selectedPage = EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_PREVIEW_TEMPLATE
          break
        }
        case TEMPLATE_BUILDER_CONTEXT_MENU_IDs.SEE_VERSION_HISTORY: {
          appState.selectedPage = EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_TEMPLATE_VERSIONS
          break
        }
        case TEMPLATE_BUILDER_CONTEXT_MENU_IDs.SAVE_TEMPLATE_SETTINGS: {
          openSaveTemplateSettings.value = true
          builderState.unsavedChanges && saveCampaignTemplate()
          break
        }
      }
    }
    async function saveCampaignTemplate() {
      saveEditor()
    }

    async function saveAsNewTemplate(name: string) {
      if (!showCloneTemplateName.value) {
        showCloneTemplateName.value = true
      } else {
        showCloneTemplateName.value = false
        saveEditor(true, true, name)
      }
    }

    const hasRSSElement = computed(() => builderState.hasRSSElement)
    const previewUrl = computed(() => builderState.previewUrl)
    const templateId = computed(() => (appState.templateId as string) || '')
    const tokenId = computed(() => store?.state?.tokenId || undefined)
    const userId = computed(() => (store?.state?.userId as string) || undefined)
    const locationId = computed(
      () => (appState.locationId as string) || undefined
    )
    const templateOptions = computed(() => {
      const options = [...getBuilderContextActionsMenu(true)]
      if (builderState.updatedAt) {
        options[2].description = `(${t('common.lastSave', {
          date: builderState.updatedAt,
        })})`
      } else if (store.state?.templateData?.lastUpdated) {
        const formattedDate = formatDate(
          store.state?.templateData?.lastUpdated,
          'MMM DD, YYYY hh:mm a'
        )
        options[2].description = options[2].description = `(${t(
          'common.lastSave',
          {
            date: formattedDate,
          }
        )})`
      }
      return options
    })
    const customValues = computed(() => store.state.customValuesLocation)
    const customFields = computed(() => store.state.customFieldsLocation)
    watch(
      () => appState.selectedPage,
      (newVal, oldVal) => {
        if (newVal) currentRouteName.value = newVal as string
      }
    )

    watch(
      () => appState.templateName,
      val => {
        // update only once
        if (val) templateName.value = val
      }
    )
    const handleModalClose = () => {
      emitter.emit('email_preview-close-builder-modal', {
        clonedTemplateId: appState.templateId,
      })
    }
    return {
      saveEditor,
      routeBack,
      templateName,
      builderState,
      onTemplateNameChange,
      templateOptions,
      currentRouteName,
      onBack,
      onMenuItemClick,
      openTestEmailSideCover,
      hasRSSElement,
      previewUrl,
      templateId,
      tokenId,
      userId,
      service,
      locationId,
      saveCampaignTemplate,
      routeNames: EMAIL_PREVIEW_ROUTES_NAMES,
      customValues,
      customFields,
      openSaveTemplateSettings,
      isModalDialog,
      handleModalClose,
      templateData,
      saveAsNewTemplate,
      cloneTemplate,
      showCloneTemplateName,
    }
  },
})
</script>

<style scoped></style>
